<template>
  <RfSimpleCard style="min-height: 400px" class="p-10">
    <div>
      <div class="rf-settings-section-title">
        <h4>Zone Manager</h4>
        <p class="title-blurb">
          Create and manage placement zones. Only zones with no prompts may be deleted.
        </p>
      </div>
      <v-table class="rf-settings-table-data mt-10">
        <thead>
          <th class="zonetablecol-1">Name & Description</th>
          <th class="zonetablecol-2">Organization</th>
          <th class="zonetablecol-3">Dimensions</th>
          <th class="zonetablecol-4">Identifier</th>
          <th class="zonetablecol-5">Type</th>
          <th class="zonetablecol-6">Device</th>
          <th class="zonetablecol-7">Prompts</th>
          <th class="zonetablecol-8"></th>
        </thead>
        <tbody v-if="filteredPathGroups.length > 0">
          <tr v-for="(pathGroup, index) in filteredPathGroups" :key="index">
            <td>
              <div class="pl-info-det">
                <h4>
                  <div v-if="isCurrOrg(pathGroup)">
                    <router-link :to="getItemUrl(pathGroup)">
                      {{ pathGroup.name }}
                    </router-link>
                  </div>
                  <div v-else>{{ pathGroup.paths.length }}</div>
                </h4>
                <span v-if="pathGroup.description">{{ pathGroup.description }}</span>
              </div>
            </td>
            <td>{{ pathGroup.org_name }}</td>
            <td>{{ pathGroup.settings.iab }}</td>
            <td>
              <div v-if="pathGroup.zone" class="pathgroup-zone-id-ctr">
                <span class="my-auto">
                  {{ renderIdentifier(pathGroup) }}
                </span>
                <v-btn
                  class="copy-paste-button"
                  size="small"
                  variant="text"
                  @click="copyToClipboard(renderIdentifier(pathGroup))"
                >
                  <v-icon size="16" class="copy-paste-icon">mdi:mdi-content-copy</v-icon>
                </v-btn>
              </div>
            </td>
            <td>{{ showPromoType(pathGroup) }}; {{ capitalize(pathGroup.zone_type) }}</td>
            <td>{{ promoDeviceType(pathGroup) }}</td>
            <td>
              <div v-if="isCurrOrg(pathGroup)">
                <router-link :to="getItemUrl(pathGroup)">
                  {{ pathGroup.paths.length }}
                </router-link>
              </div>
              <div v-else>{{ pathGroup.paths.length }}</div>
            </td>
            <td>
              <div class="settings-button-wrapper">
                <RfButton
                  :icon="
                    !!userStore.isDisabledWithMessage ? 'mdi:mdi-information-variant' : 'md:edit'
                  "
                  color="accent"
                  variant="plain"
                  density="compact"
                  :disabled="!isCurrOrg(pathGroup)"
                  @click="openEditDialog(pathGroup)"
                />
                <RfButton
                  icon="md:clear"
                  variant="plain"
                  density="compact"
                  color="error"
                  :disabled="
                    userStore.isDisabledWithMessage ||
                    !isCurrOrg(pathGroup) ||
                    disabledState(pathGroup)
                  "
                  @click="openDeleteDialog(pathGroup)"
                />
              </div>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td colspan="7">
              <div class="settings-no-data-message">Nothing to show</div>
            </td>
          </tr>
        </tbody>
      </v-table>
      <v-dialog v-model="showDeleteConfirmationDialog" persistent width="500">
        <v-card>
          <v-card-title class="headline">Are you sure you want to delete?</v-card-title>
          <v-card-text>
            Deleting this zone removes all information about this zone. This cannot be undone.
          </v-card-text>
          <v-card-actions class="modal-card-actions-div">
            <v-btn class="cancel-btn px-7!" variant="outlined" @click="closeDeleteDialog">
              Cancel
            </v-btn>
            <v-btn variant="flat" color="error" class="px-10!" @click="submitDelete">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="showEditDialog" persistent width="640">
        <v-card>
          <v-card-title class="headline">Edit zone</v-card-title>
          <v-card-text v-if="pathGroupItem" class="placement-zone-editform">
            <RfZoneId
              ref="placementZone"
              :key="pathGroupItem.id"
              :model="pathGroupItem"
              showFullModal
            />
          </v-card-text>
          <v-card-actions class="modal-card-actions-div">
            <v-btn
              size="large"
              class="cancel-btn px-7!"
              variant="outlined"
              @click="closeEditDialog"
            >
              {{ !userStore.isDisabledWithMessage ? "Cancel" : "Close" }}
            </v-btn>
            <RfButton
              buttonText="Submit"
              color="accent"
              class="px-10!"
              size="large"
              :disabled="userStore.isDisabledWithMessage || !isZoneIsValid"
              @click="submitEdit"
            />
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="editConfirmDialog" persistent width="500">
        <v-card>
          <v-card-title class="headline">Are you sure?</v-card-title>
          <v-card-text>
            Changing from a live zone to a code zone is a one-way change. Changing from code to live
            is not allowed.
          </v-card-text>
          <v-card-actions class="modal-card-actions-div">
            <v-btn class="cancel-btn px-7!" variant="outlined" @click="closeEditConfirmDialog">
              Cancel
            </v-btn>
            <v-btn variant="flat" color="error" class="px-10!" @click="submitConfiguration">
              Continue
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <RfCreateNewPlacement v-model="showCreateModal" />
    </div>
  </RfSimpleCard>
</template>

<script>
import RfSimpleCard from "@/components/RfSimpleCard.vue";
import RfZoneId from "@/components/RfCommonCards/RfZoneId.vue";
import RfCreateNewPlacement from "@/components/RfPlacements/RfCreateNewPlacement.vue";
import copyToClipboard from "@/utils/CopyToClipboard";
import PromoTypeMixin from "@/utils/PromoTypeMixin";
import RfButton from "@/components/buttons/RfButton.vue";
import { cloneDeep } from "lodash-es";
import StringUtils from "@/utils/StringUtils";
import { useToastsStore } from "@/pinia/toastsStore";
import { ref } from "vue";
import { isStandalone } from "@/utils/prompts/promptHelpers";
import { usePromptStore } from "@/pinia/promptStore";
import { useUserStore } from "@/pinia/userStore";
import { useAllPromptsStore } from "@/pinia/allPromptsStore";
import { useOrgsStore } from "@/pinia/orgsStore";

export default {
  name: "RfPlacementZones",
  components: { RfSimpleCard, RfCreateNewPlacement, RfZoneId, RfButton },
  mixins: [PromoTypeMixin],
  setup: () => ({
    placementZone: ref(),
    capitalize: StringUtils.capitalize,
    toastsStore: useToastsStore(),
    orgsStore: useOrgsStore(),
    allPromptsStore: useAllPromptsStore(),
    promptStore: usePromptStore(),
    userStore: useUserStore(),
  }),
  data() {
    return {
      showDeleteConfirmationDialog: false,
      showCreateModal: false,
      showEditDialog: false,
      pathGroupItem: null,
      zone: null,
      editConfirmDialog: false,
      copyToClipboard,
    };
  },
  computed: {
    isZoneIsValid() {
      return this.placementZone?.isValid;
    },
    filteredPathGroups() {
      const groups = this.allPromptsStore.promptGroups.filter(pg => !isStandalone(pg));
      return groups.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    },
  },
  mounted() {
    this.allPromptsStore.getPromptGroups(this.$route.params.aid);

    if (this.$route.hash !== "#modal") return;
    this.$router.push({ hash: "" });
    this.showAddDialog();
  },
  methods: {
    submitEdit() {
      if (this.originalItem.zone_type === "live" && this.$refs.placementZone.zoneType === "code") {
        this.openEditConfirmDialog();
      } else {
        this.submitConfiguration();
      }
    },
    openEditConfirmDialog() {
      this.editConfirmDialog = true;
    },
    closeEditConfirmDialog() {
      this.editConfirmDialog = false;
    },
    showAddDialog() {
      this.showCreateModal = true;
    },
    isCurrOrg(item) {
      return this.orgsStore.editableOrgs.indexOf(item.org_id) > -1;
    },
    disabledState(item) {
      return item.paths.length > 0;
    },
    renderIdentifier(item) {
      if (item.device_type === "web") {
        return `<div data-rf-zone="${item.zone}"></div>`;
      }
      return item.zone;
    },
    promoDeviceType(item) {
      return this.getPromoDeviceTypeWording(item.device_type);
    },
    showPromoType(item) {
      return this.getPromoTypeWording(item.path_type);
    },
    getItemUrl(item) {
      return { path: `/apps/${this.$route.params.aid}/placements/${item.id}` };
    },
    openDeleteDialog(item) {
      this.showDeleteConfirmationDialog = true;
      this.pathGroupItem = item;
    },
    closeDeleteDialog() {
      this.showDeleteConfirmationDialog = false;
    },
    closeEditDialog() {
      this.showEditDialog = false;
      this.pathGroupItem = null;
    },
    openEditDialog(item) {
      this.originalItem = cloneDeep(item);
      this.showEditDialog = true;
      this.pathGroupItem = item;
    },
    submitDelete() {
      this.promptStore
        .deletePathGroup({
          appId: this.$route.params.aid,
          pathGroupId: this.pathGroupItem.id,
        })
        .then(() => {
          this.showDeleteConfirmationDialog = false;
          this.toastsStore.create({ type: "success", body: "Zone deleted successfully" });
        })
        .catch(() => null);
    },
    async submitConfiguration() {
      try {
        const newPath = cloneDeep(this.pathGroupItem);
        if (this.$refs.placementZone) {
          this.$refs.placementZone.fillModel(newPath);
        }
        if (!newPath.name) throw new Error("Name field can't be empty");
        if (!newPath.zone) throw new Error("Identifier field can't be empty");
        this.closeEditDialog();
        this.originalItem = null;
        this.closeEditConfirmDialog();
        await this.promptStore.updatePathGroup({ appId: this.$route.params.aid, model: newPath });
        this.toastsStore.create({ type: "success", body: "Zone updated successfully" });
      } catch (e) {
        this.toastsStore.create({ type: "error", body: e.message });
      }
    },
    click() {
      this.showAddDialog();
    },
  },
};
</script>
<style scoped>
.zonetablecol-1 {
  width: 31%;
}
.zonetablecol-2 {
  width: 10%;
}
.zonetablecol-3 {
  width: 31%;
}
.zonetablecol-4 {
  width: 10%;
}
.zonetablecol-5 {
  width: 17%;
}
.zonetablecol-6 {
  width: 10%;
}
.zonetablecol-7 {
  width: 10%;
}
.zonetablecol-8 {
  width: 10%;
}
</style>
